import { Input } from "@/components/ui/input";
import { useSearchContext } from "@/contexts/search-context";
import { cn } from "@/lib/utils";
import { DataService } from "@/services/data";
import { useDebounce } from "@/utils/misc";
import * as LucideIcons from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { SearchResultItem } from "../layout-2/types2";
import { SearchType } from "@/types/crm/search";
import { looksLikeSalesforceId } from "@/utils/salesforce-id";
import {
    chunkSearchTypes,
    fetchSearchLightMulti,
    filterSearchRecents,
    loadSearchRecents,
    rememberSearchClick,
    SEARCH_TIMEOUT_MESSAGE,
    SearchRecentItem
} from "@/utils/search-light";
import { useUser } from "@/providers/user";
import { useAuth } from "@/providers/auth";

interface SearchBoxProps {
    className?: string;
    placeholder?: string;
}

function ResultRow({
    item,
    icon,
    color,
    onRemember
}: {
    item: SearchResultItem;
    icon?: string;
    color?: string;
    onRemember: (item: SearchRecentItem) => void;
}) {
    const IconComponent = icon && (LucideIcons as any)[icon] ? (LucideIcons as any)[icon] : LucideIcons["Search"];
    return (
        <Link
            to={item.href}
            onClick={() => onRemember({ ...item, icon, color })}
            className="block w-full rounded-md px-3 py-1 text-left transition-colors hover:bg-accent focus:bg-accent focus:outline-none"
        >
            <div className="flex items-center space-x-3">
                <span className={cn("flex h-6 w-6 items-center justify-center rounded")} style={{ backgroundColor: color }}>
                    {IconComponent && <IconComponent className="h-4 w-4 text-white" strokeWidth={1.5} />}
                </span>
                <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{item.title}</p>
                    {item.subtitle && <p className="truncate text-xs text-muted-foreground">{item.subtitle}</p>}
                </div>
            </div>
        </Link>
    );
}

const SearchResults = ({
    query,
    searchByType,
    recents,
    isDebouncing,
    onRemember
}: {
    query: string;
    searchByType: Record<string, any>;
    recents: SearchRecentItem[];
    isDebouncing: boolean;
    onRemember: (item: SearchRecentItem) => void;
}) => {
    const hasResults = Object.values(searchByType).some(group => group && group.results && group.results.length > 0);
    const isSearching = isDebouncing || Object.values(searchByType).some(group => group && group.loading);
    const allLoaded = Object.values(searchByType).every(group => group && !group.loading);
    const hasErrors = Object.values(searchByType).some(group => group && group.error);
    const visibleRecents = filterSearchRecents(recents, query);
    const hasLiveSearch = Object.keys(searchByType).length > 0;

    return (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-96 overflow-y-auto rounded-md border bg-white shadow-lg">
            <div className="p-2">
                {visibleRecents.length > 0 && (
                    <div className="mb-2 space-y-1">
                        <div className="px-2 py-1 text-xs font-semibold uppercase text-muted-foreground">Recent</div>
                        {visibleRecents.map(item => (
                            <ResultRow key={item.href} item={item} icon={item.icon} color={item.color} onRemember={onRemember} />
                        ))}
                    </div>
                )}
                {!hasLiveSearch && isDebouncing ? (
                    <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
                        <LucideIcons.Loader className="h-4 w-4 animate-spin text-blue-500" />
                        <span className="text-sm">Searching...</span>
                    </div>
                ) : hasLiveSearch ? (
                    <div className="space-y-4">
                        {/* Indicateur de statut de recherche */}
                        {isSearching && (
                            <div className="flex items-center justify-center gap-2 py-2">
                                <LucideIcons.Loader className="h-4 w-4 animate-spin text-blue-500" />
                                <span className="text-xs text-muted-foreground">{hasResults ? "Searching for more results..." : "Searching..."}</span>
                            </div>
                        )}

                        {/* Affichage des résultats par type */}
                        {Object.entries(searchByType)
                            .filter(([_, group]) => group && group.results && group.results.length > 0)
                            .sort((a, b) => (a[1].priority || 100) - (b[1].priority || 100))
                            .map(([type, group]) => {
                                const IconComponent = group.icon && (LucideIcons as any)[group.icon] ? (LucideIcons as any)[group.icon] : LucideIcons["Search"];
                                return (
                                    <div key={type}>
                                        <div className="mb-3 mt-2 flex items-center justify-between px-2">
                                            <div className="flex items-center">
                                                <span className={cn("mr-2 flex h-5 w-5 items-center justify-center rounded")} style={{ backgroundColor: group.color }}>
                                                    {IconComponent && <IconComponent className="h-3.5 w-3.5 text-white" strokeWidth={1.5} />}
                                                </span>
                                                <span className="text-sm font-semibold">
                                                    {group.model_name_plural || type} ({group.results.length})
                                                </span>
                                            </div>
                                            {group.loading && <LucideIcons.Loader className="h-3 w-3 animate-spin text-blue-500" />}
                                        </div>
                                        <div className="space-y-1">
                                            {group.results.map((item: SearchResultItem) => (
                                                <ResultRow
                                                    key={item.id}
                                                    item={item}
                                                    icon={group.icon}
                                                    color={group.color}
                                                    onRemember={onRemember}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}

                        {/* Message si aucun résultat après chargement complet */}
                        {allLoaded && !hasResults && visibleRecents.length === 0 && (
                            <div className="py-8 text-center text-muted-foreground">
                                <p>No results found for "{query}"</p>
                                <p className="mt-1 text-sm">Try other keywords</p>
                            </div>
                        )}

                        {/* Affichage des erreurs */}
                        {hasErrors && <div className="py-2 text-center text-xs text-red-500">Some search types encountered errors</div>}

                        {/* Bouton "Voir tous les résultats" seulement s'il y a des résultats */}
                        {hasResults && (
                            <>
                                <hr className="my-2" />
                                <button className="group w-full rounded-md px-3 py-2 text-left text-sm text-primary transition-colors hover:bg-accent">
                                    <Link to={`/search?q=${encodeURIComponent(query)}`}>
                                        See full results for "{query}" <LucideIcons.MoveRight className="invisible ml-2 inline-block h-4 w-4  group-hover:visible" />
                                    </Link>
                                </button>
                            </>
                        )}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export function SearchBox({ className = "", placeholder = "Search" }: SearchBoxProps) {
    const [query, setQuery] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [typesMeta, setTypesMeta] = useState<SearchType[]>([]); // [{type, icon, color, ...}]
    const [searchByType, setSearchByType] = useState<Record<string, any>>({});
    const [recents, setRecents] = useState<SearchRecentItem[]>(() => loadSearchRecents());
    const [isDebouncing, setIsDebouncing] = useState(false);
    const searchRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const searchRequestId = useRef(0);
    const searchAbortController = useRef<AbortController | null>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useUser();
    const { viewAsActive } = useAuth();

    // Debounce la requête de recherche
    const debouncedQuery = useDebounce(query, 300);

    // Enregistrer la SearchBox dans le context global
    const { registerSearchBox } = useSearchContext();

    // Fetch la liste des types au mount
    useEffect(() => {
        DataService.client.url("/crm/app/search/types/").get().json<SearchType[]>().then(setTypesMeta);
    }, []);

    // Effectuer la recherche quand la requête debounced change
    useEffect(() => {
        if (debouncedQuery.length > 0) {
            performSearchByType(debouncedQuery);
        } else {
            setSearchByType({});
        }
    }, [debouncedQuery]);

    // Enregistrer l'input dans le context au montage
    useEffect(() => {
        registerSearchBox(inputRef);
    }, [registerSearchBox]);

    useEffect(() => () => searchAbortController.current?.abort(), []);

    useEffect(() => {
        searchRequestId.current += 1;
        searchAbortController.current?.abort();
        setQuery("");
        setSearchByType({});
        setIsDebouncing(false);
        setIsOpen(false);
        setRecents(loadSearchRecents());
    }, [user?.id]);

    useEffect(() => {
        searchRequestId.current += 1;
        searchAbortController.current?.abort();
        setQuery("");
        setSearchByType({});
        setIsDebouncing(false);
        setIsOpen(false);
        setRecents([]);
    }, [viewAsActive]);

    // Fermer les suggestions quand on clique ailleurs
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setIsFocused(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        setIsOpen(false);
    }, [location.pathname]);

    // Gérer les changements de recherche
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setQuery(value);
        searchRequestId.current += 1;
        searchAbortController.current?.abort();
        setSearchByType({});
        setIsDebouncing(value.length >= 2);

        if (value.length >= 2 || filterSearchRecents(recents, value).length > 0) {
            setIsOpen(true);
        } else {
            setIsOpen(false);
        }
    };

    // Nouvelle fonction pour rechercher par type (comme dans search-page.tsx)
    const performSearchByType = async (searchQuery: string) => {
        if (!searchQuery || searchQuery.length < 2 || typesMeta.length === 0) {
            setIsDebouncing(false);
            return;
        }
        const requestId = ++searchRequestId.current;
        searchAbortController.current?.abort();
        const controller = new AbortController();
        searchAbortController.current = controller;
        setIsDebouncing(false);

        const newState: Record<string, any> = {};
        const enabledTypes = typesMeta.filter(typeMeta => typeMeta.is_light_search_enabled);
        enabledTypes.forEach(typeMeta => {
            newState[typeMeta.type] = {
                loading: true,
                error: null,
                results: [],
                icon: typeMeta.icon,
                color: typeMeta.color,
                model_name: typeMeta.model_name,
                model_name_plural: typeMeta.model_name_plural,
                priority: typeMeta.priority
            };
        });
        setSearchByType(newState);

        await Promise.all(
            chunkSearchTypes(enabledTypes).map(async chunk => {
                try {
                    const { results, errors } = await fetchSearchLightMulti(
                        searchQuery,
                        chunk.map(typeMeta => typeMeta.type),
                        controller
                    );
                    if (requestId !== searchRequestId.current) return;
                    setSearchByType(prev => {
                        const next = { ...prev };
                        for (const typeMeta of chunk) {
                            const typeData = results[typeMeta.type];
                            next[typeMeta.type] = {
                                ...prev[typeMeta.type],
                                loading: false,
                                error: errors[typeMeta.type] ? SEARCH_TIMEOUT_MESSAGE : null,
                                results: typeData?.results || [],
                                icon: typeData?.icon ?? typeMeta.icon,
                                color: typeData?.color ?? typeMeta.color,
                                model_name: typeData?.model_name ?? typeMeta.model_name,
                                model_name_plural: typeData?.model_name_plural ?? typeMeta.model_name_plural
                            };
                        }
                        return next;
                    });
                } catch (err) {
                    if (requestId !== searchRequestId.current) return;
                    setSearchByType(prev => {
                        const next = { ...prev };
                        for (const typeMeta of chunk) {
                            next[typeMeta.type] = {
                                ...prev[typeMeta.type],
                                loading: false,
                                error: err instanceof Error ? err.message : "Error while searching"
                            };
                        }
                        return next;
                    });
                }
            })
        );
    };

    // Gérer le focus sur le champ de recherche
    const handleRemember = (item: SearchRecentItem) => {
        setRecents(rememberSearchClick(item));
    };

    const handleInputFocus = () => {
        setIsFocused(true);
        const latestRecents = loadSearchRecents();
        setRecents(latestRecents);
        if (query.length >= 2 || filterSearchRecents(latestRecents, query).length > 0) {
            setIsOpen(true);
        }
    };

    // Gérer la perte de focus
    const handleInputBlur = () => {
        setTimeout(() => {
            if (!searchRef.current?.contains(document.activeElement)) {
                setIsOpen(false);
                setIsFocused(false);
            }
        }, 150);
    };

    // Gérer le hover
    const handleMouseEnter = () => {
        setIsHovered(true);
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
    };

    // Gérer la soumission du formulaire
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = query.trim();
        if (!trimmed) {
            return;
        }
        // Paste of a Salesforce id → jump straight to the record.
        if (looksLikeSalesforceId(trimmed)) {
            navigate(`/sf/${encodeURIComponent(trimmed)}`);
        } else {
            navigate(`/search?q=${encodeURIComponent(trimmed)}`);
        }
        setIsOpen(false);
        setIsFocused(false);
        inputRef.current?.blur();
    };

    // Déterminer si la boîte de recherche doit être élargie
    const isExpanded = isHovered || isFocused || query.length > 0 || isOpen;

    return (
        <div
            ref={searchRef}
            data-search-box // Marqueur pour le hook de raccourci clavier
            className={cn("rounded-lg bg-white transition-all duration-300 ease-in-out", isExpanded ? "relative z-20" : "relative", isExpanded ? "w-[500px]" : "w-[400px]", className)}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <form onSubmit={handleSubmit} className="relative">
                <LucideIcons.Search className="absolute top-1/2 ml-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                    ref={inputRef}
                    type="text"
                    placeholder={`${placeholder}`}
                    value={query}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    className={cn(
                        "w-full pl-9 pr-16 transition-all duration-300" // Augmenté pr pour faire place au badge
                    )}
                    autoComplete="off"
                />
                {/* Badge indicateur de raccourci clavier */}
                <div className="absolute right-3 top-1/2 hidden -translate-y-1/2 items-center gap-1 text-xs text-muted-foreground sm:flex">
                    <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
                        <span className="text-xs">⌘</span>K
                    </kbd>
                </div>
            </form>

            {isOpen && (
                <SearchResults
                    query={query}
                    searchByType={searchByType}
                    recents={recents}
                    isDebouncing={isDebouncing}
                    onRemember={handleRemember}
                />
            )}
        </div>
    );
}
